// Settings → Shopify (admins): is the store connected, what did the last pulls do, and run one now.
import { CircleAlert, CircleCheck, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { ago, qty } from "../lib/format.ts";
import { mutate } from "../lib/mutate.ts";
import { Link } from "../lib/router.tsx"; // Consignment ---
import { useRows } from "../lib/sync.ts";
import { Badge, Button } from "../ui/controls.tsx";
import { toast } from "../ui/toast.tsx";

type Job = {
  job: string;
  started_at: string;
  finished_at: string | null;
  ok: boolean | null;
  summary: Record<string, unknown>;
  /** When it last worked (null = never). */
  last_ok_at: string | null;
};
type Status = {
  configured: boolean;
  shop: string | null;
  connected: boolean;
  installedAt: string | null;
  missingScopes: string[];
  products: { reason: string; n: number }[];
  jobs: Job[];
  webhooks: { pending: number; failed: number; last_received: string | null; failedEvents: { topic: string; error: string; received_at: string }[] };
  running: string[];
};

const JOBS = [
  { job: "catalog", label: "Catalog", what: "Every product and variant (nightly)" },
  { job: "stock", label: "Stock", what: "On hand and available at each location (every 6 hours)" },
  { job: "locations", label: "Locations", what: "Shopify locations" },
  { job: "webhooks", label: "Webhooks", what: "Live updates from Shopify" },
  { job: "sales", label: "Sales", what: "Units sold per variant per day, from Shopify's reports (nightly)" },
  { job: "stock-history", label: "Stock history", what: "Days each variant was out of stock at HHL, from Shopify's reports (nightly)" },
  { job: "forecast", label: "Forecast", what: "Base rates, seasonality, trend and accuracy (after each sales or stock-history pull)" },
  // Make planning ---
  { job: "auto-reorder", label: "Auto mode", what: "Draft POs for items set to auto-reorder, never sent (after each forecast)" },
];

/** Started but not finished in the last hour: running, possibly from the CLI, which this server's `running` list can't see. Older ones were cut off (e.g. by a restart). */
const unfinished = (j: Job | undefined) => !!j && !j.finished_at && Date.now() - Date.parse(j.started_at) < 3_600_000;

const summaryText = (s: Record<string, unknown>) =>
  s.error
    ? String(s.error)
    : Object.entries(s)
        .map(([k, v]) => `${v} ${k}`)
        .join(" · ");

export function ShopifyPage() {
  const [status, setStatus] = useState<Status | null>(null);
  const load = useCallback(async () => {
    const res = await fetch("/api/shopify/status");
    if (res.ok) setStatus(await res.json());
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  // Background jobs aren't synced data, so refresh this page while one runs.
  const anyRunning = !!status && (status.running.length > 0 || status.jobs.some(unfinished));
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [anyRunning, load]);

  const run = async (job: string) => {
    const res = await fetch("/api/shopify/run", {
      method: "POST",
      headers: { "content-type": "application/json", "x-requested-with": "hhl-ops" },
      body: JSON.stringify({ job }),
    });
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) return toast.error(body.error ?? "Couldn't start that");
    toast.ok("Started");
    setTimeout(load, 500);
  };

  if (!status) return null;
  const jobs = new Map(status.jobs.map((j) => [j.job, j]));
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl px-8 py-8">
        <p className="text-muted">
          HHL Ops mirrors the store's products, variants, stock and locations. Edit products here (Sell items → Edit) or in Shopify; each side gets the other's
          changes within seconds.
        </p>

        <div className="mt-6 flex items-start gap-3 rounded-lg border border-line bg-surface p-4">
          {status.connected ? <CircleCheck className="mt-0.5 size-5 text-ok" /> : <CircleAlert className="mt-0.5 size-5 text-warn" />}
          <div className="flex-1">
            {!status.configured ? (
              <>
                <div className="font-medium">Not set up on the server</div>
                <div className="text-muted">
                  The HHL Ops Shopify app's client ID and secret go in /opt/hhl/hhl-ops.env (run /opt/hhl/tools/hhl-ops-shopify-keys.sh).
                </div>
              </>
            ) : status.connected ? (
              <>
                <div className="font-medium">Connected to {status.shop}</div>
                <div className="text-muted">Since {status.installedAt ? new Date(status.installedAt).toLocaleString() : "—"}</div>
                {status.missingScopes.length > 0 && (
                  <div className="mt-2 text-warn">
                    Missing permissions: {status.missingScopes.join(", ")}. Open HHL Ops from Shopify admin → Apps to approve them.
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="font-medium">Not connected yet</div>
                <div className="text-muted">
                  Open the custom install link from the Shopify Dev Dashboard (or HHL Ops in Shopify admin → Apps) and approve it.
                </div>
              </>
            )}
          </div>
        </div>

        <h2 className="mt-8 mb-2 font-semibold">Pulls</h2>
        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {JOBS.map(({ job, label, what }) => {
            const last = jobs.get(job);
            const running = status.running.includes(job) || unfinished(last);
            const outcome = last?.ok ? "Done" : last?.ok === false ? "Failed" : "Didn't finish";
            return (
              <div key={job} className="flex items-center gap-3 border-b border-line px-4 py-2.5 last:border-0">
                <div className="w-28 font-medium">{label}</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-muted">{what}</div>
                  {last && (
                    <div className="truncate text-[12px] text-faint">
                      {running ? `Running since ${ago(last.started_at)}…` : `${outcome} ${ago(last.finished_at ?? last.started_at)}`}
                      {!running && last.ok === false && ` · last worked ${last.last_ok_at ? ago(last.last_ok_at) : "never"}`}
                      {!running && last.ok !== null && ` · ${summaryText(last.summary)}`}
                    </div>
                  )}
                </div>
                {last && !running && (
                  <Badge tone={last.ok ? "ok" : last.ok === false ? "danger" : "warn"}>{last.ok ? "OK" : last.ok === false ? "Failed" : "Didn't finish"}</Badge>
                )}
                <Button
                  size="sm"
                  disabled={!status.connected || running}
                  icon={<RefreshCw className={running ? "size-3.5 animate-spin" : "size-3.5"} />}
                  onClick={() => run(job)}
                >
                  {running ? "Running" : "Run now"}
                </Button>
              </div>
            );
          })}
        </div>

        <Locations />

        <div className="mt-8 grid grid-cols-2 gap-4">
          <div>
            <h2 className="mb-2 font-semibold">Products</h2>
            <div className="overflow-hidden rounded-lg border border-line bg-surface">
              {!status.products.length && <div className="p-4 text-muted">None yet.</div>}
              {status.products.map((p) => (
                <div key={p.reason} className="flex justify-between border-b border-line px-4 py-2 last:border-0">
                  <span className={p.reason === "Mirrored" ? "font-medium" : "text-muted"}>
                    {p.reason === "Mirrored" ? "Mirrored as items" : `Left out: ${p.reason}`}
                  </span>
                  <span className="font-mono text-[12px]">{p.n}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="mb-2 font-semibold">Webhooks</h2>
            <div className="rounded-lg border border-line bg-surface p-4">
              <div className="text-muted">Last received {status.webhooks.last_received ? ago(status.webhooks.last_received) : "never"}</div>
              <div className="mt-1">
                {status.webhooks.pending} waiting · {status.webhooks.failed} failed
              </div>
              {status.webhooks.failedEvents.map((e) => (
                <div key={e.received_at + e.topic} className="mt-2 truncate text-[12px] text-danger" title={e.error}>
                  {e.topic}: {e.error}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Which Shopify locations HHL Ops uses. Ignored ones (tests, events) bring in no stock. */
function Locations() {
  const locations = useRows("location").filter((l) => l.shopify_location_id);
  const stock = useRows("stock_level");
  if (!locations.length) return null;
  const sorted = [...locations].sort((a, b) => Number(a.ignored) - Number(b.ignored) || a.name.localeCompare(b.name));
  return (
    <>
      <h2 className="mt-8 mb-2 font-semibold">Locations</h2>
      <div className="overflow-hidden rounded-lg border border-line bg-surface">
        {sorted.map((l) => {
          const here = stock.filter((s) => s.location_id === l.id);
          const units = here.reduce((n, s) => n + Number(s.on_hand), 0);
          return (
            <label key={l.id} className="flex cursor-pointer items-center gap-3 border-b border-line px-4 py-2.5 last:border-0 hover:bg-hover">
              <input
                type="checkbox"
                checked={!l.ignored}
                onChange={(e) => mutate("location.update", { id: l.id, patch: { ignored: !e.target.checked } })}
                className="accent-[var(--accent)]"
              />
              <span className={l.ignored ? "text-faint line-through" : "font-medium"}>{l.name}</span>
              {!l.active && <Badge>Inactive in Shopify</Badge>}
              {/* Consignment --- a CM's site, or one that could be */}
              {l.is_cm ? (
                <Link to={`/consignment?cm=${l.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                  <Badge tone="accent">CM site</Badge>
                </Link>
              ) : (
                l.ignored && (
                  <Link to={`/consignment?setup=${l.id}`} className="text-[12px] text-accent-text hover:underline" onClick={(e) => e.stopPropagation()}>
                    Make it a CM's site
                  </Link>
                )
              )}
              <span className="ml-auto text-[12px] text-faint">{l.ignored ? "Not used" : `${qty(here.length)} items · ${qty(units)} on hand`}</span>
            </label>
          );
        })}
      </div>
      <p className="mt-2 text-[12px] text-faint">Untick test or event locations: HHL Ops drops their stock and ignores updates from them.</p>
    </>
  );
}
